import { LookupValue, LookupValueRequest } from "../../../../../models/lookups/lookup.models";
import { OnboardingRequestModel, OfferingMetaBagModel, OnboardingRequestStepEnum, OnboardingRequestVerificationModel, OfferingModel, OnboardingRequestDocumentModel } from "../../../../../models/onboarding/onboarding.models";
import { PropertyMetaModel, AllEntityTypeConfigurationConstants } from "../../../../../models/basics/basics.models";
import { bindable, computedFrom, autoinject, transient, useView } from "aurelia-framework";
import { QueriesClientService } from "../../../../../services/queries/queries-client.services";
import { LookupClientService } from "../../../../../services/lookups/lookup-client.service";
import { OnboardingAdminService } from "../../../../../services/onboarding/onboarding-admin.service";
import { DialogServiceConstants } from "../../../../../constants/basics/dialog.constants";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { ClientMetaModel } from "../../../onboarding-client/client-meta-component/client-meta-component";
import { FileHelper } from "../../../../../helpers/file-helper";
import { AttachmentsHelper } from "../../../../../utilities/attachments-helper";
import { Reflection } from "../../../../../utilities/reflection";

@autoinject()
@transient()
@useView('views/pages/admin/onboarding/onboarding-request/onboarding-request-content.html')
export class OnboardingRequestContent {

    public afterBasicMeta: string = "";
    public onboardingHeader: string = "";
    public onboardingCustomContent: string = "";

    public basicConfigurations: ClientMetaModel[] = [];
    public advancedConfigurations: ClientMetaModel[] = [];
    public offeringsConfigurations: { [offeringId: string]: ClientMetaModel[] } = {};

    public onboardingRequestEntityType: number;
    public attachmentsTypes: LookupValue[];
    public item: OnboardingRequestModel;
    public odataModel: any;
    public offeringsMetas: OfferingMetaBagModel[];
    public entityTypeId: number = -1;
    public completedStep: number = OnboardingRequestStepEnum.Completed;
    public onboardingModelProperties: PropertyMetaModel[] = [];
    public offeringModelProperties: PropertyMetaModel[] = [];
    public isLoading: boolean = true;

    @bindable()
    onboardingRequestId: number;

    @computedFrom(Reflection.getPropertyPath<OnboardingRequestContent>(z => z.item.offeringsSelected))
    get offerings() {
        return this.item && this.item.offeringsSelected ? this.item.offeringsSelected.map(x => x.offering) : [];
    }

    constructor(private onboardingAdminService: OnboardingAdminService,
        private queriesClientService: QueriesClientService,
        private dialogService: DialogClientService,
        private element: Element,
        private lookupClientService: LookupClientService) { }

    onboardingRequestIdChanged() {
        const promise1 = this.onboardingAdminService.getOnboardingRequest(this.onboardingRequestId);
        const promise2 = this.lookupClientService.getLookup(new LookupValueRequest("attachmentTypes"));
        Promise.all([promise1, promise2]).then(results => {
            const entity = AllEntityTypeConfigurationConstants.filter(x => x.entityType == results[0].entityTypeId)[0];
            this.queriesClientService.getData(`odata/${entity.uri}?$filter=onboardingRequestId eq ${this.onboardingRequestId}`).then(response => {
                this.odataModel = response.value[0];
                this.isLoading = false;
            });
            this.entityTypeId = entity.entityType;
            this.onboardingModelProperties = results[0].onboardingModelProperties;
            this.offeringModelProperties = results[0].offeringModelProperties;
            this.onboardingRequestEntityType = results[0].entityTypeId;
            this.item = results[0].onboardingRequest;
            this.offeringsMetas = this.item.offeringsMeta ? JSON.parse(this.item.offeringsMeta) : [];
            this.attachmentsTypes = results[1].allValuesIncludingInactive;
            this.afterBasicMeta = (results[0].onboardingAfterBasicMetaPath || '').replace(/\\/g, '/');
            this.onboardingHeader = (results[0].onboardingHeaderPath || '').replace(/\\/g, '/');
            this.onboardingCustomContent = (results[0].onboardingCustomContentPath || '').replace(/\\/g, '/');

            this.basicConfigurations = [
                {
                    isMetaFormStyle: this.item.onboardingConfig.isBasicMetaFormStyle,
                    config: this.item.onboardingConfig.basicMetaConfig,
                    model: this.model,
                    properties: this.onboardingModelProperties,
                    fallbackTitle: '',
                    fallbackSubtitle: '',
                    doNotUseFallback: false,
                    rules: this.item.rules
                }
            ];
            this.advancedConfigurations = [
                {
                    isMetaFormStyle: this.item.onboardingConfig.isAdvancedMetaFormStyle,
                    config: this.item.onboardingConfig.advancedMetaConfig,
                    model: this.model,
                    properties: this.onboardingModelProperties,
                    fallbackTitle: '',
                    fallbackSubtitle: '',
                    doNotUseFallback: false,
                    rules: this.item.rules
                }
            ];

            this.offerings.forEach(offering => {
                this.offeringsConfigurations[offering.offeringId] = [{
                    isMetaFormStyle: offering.isMetaFormStyle,
                    config: offering.metaConfig,
                    model: this.getOfferingModel(offering),
                    properties: this.offeringModelProperties,
                    fallbackTitle: offering.name,
                    fallbackSubtitle: '',
                    doNotUseFallback: false,
                    rules: []
                }];
            });

            this.raiseOnboardingRequestReady(null, this.item);
        });
    }

    private raiseOnboardingRequestReady(oldValue: OnboardingRequestModel, newValue: OnboardingRequestModel): void {
        const paramsEvent: any = { detail: newValue, bubbles: true };
        const ev: CustomEvent = new CustomEvent("onboarding-request-ready", paramsEvent);
        this.element.dispatchEvent(ev);
    }

    getAttachmentType(attachmentTypeId: number): string {
        const item = this.attachmentsTypes.filter(x => x.id == attachmentTypeId)[0];
        return item ? item.name : '';
    }

    @computedFrom(Reflection.getPropertyPath<OnboardingRequestContent>(z => z.item.modelMeta))
    public get model(): any {
        return this.getModel(this.item.modelMeta ? this.item.modelMeta : "");
    }

    getModel(str: string) {
        try {
            return str ? JSON.parse(str) : {};
        } catch (e) {
            console.error(e);
            return {};
        }
    }

    getVerificationViewModel(verification: OnboardingRequestVerificationModel) {
        if (!verification) {
            return null;
        }
        const internalName = verification.onboardingVerificationPlugin.internalName;
        return `views/plugins/verification-plugins/${internalName}/${internalName}-meta`;
    }

    getOfferingModel(offering: OfferingModel) {
        const selectedOffering = this.offeringsMetas.filter(x => x.offeringId == offering.offeringId)[0];
        const offeringData = offering && selectedOffering ? selectedOffering.bag : {};
        $.extend(offeringData, this.model);
        return offeringData;
    }

    openDocument(document: OnboardingRequestDocumentModel) {
        if (document && document.documentPath) {
            if (this.isPDF(document.documentPath) || this.isImage(document.documentPath)) {
                this.dialogService.show(DialogServiceConstants.URLs.ObjectViewer,
                    {
                        link: document.documentPath
                    }, false, false, true);
            }
            else {
                FileHelper.downloadFile(AttachmentsHelper.getUrl(document.documentPath), document.documentName);
            }
        }
    }

    public isPDF(link): boolean {
        return link.indexOf('.pdf') != -1;
    }

    public isImage(link): boolean {
        const imageExtensions = ["jpg", "jpeg", "png", "gif", "svg"];
        return imageExtensions.some(imageExtension => link.indexOf(imageExtension) != -1);
    }
}
